import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Briefcase, UserCheck, Award, Settings, X, LogOut, Menu, ShieldAlert } from 'lucide-react';

export default function MitarbeiterLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('loading');

  useEffect(() => {
    checkStatus();
  }, [location.pathname]);

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('tester_token');
      if (!token) return navigate('/mitarbeiter/login');
      
      const response = await fetch('/api/employee/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVerificationStatus(data.verification_status);
        if (data.verification_status !== 'verified' && location.pathname !== '/mitarbeiter/verification') {
          navigate('/mitarbeiter/verification', { replace: true });
        }
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error(error);
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('tester_token');
    navigate('/mitarbeiter/login');
  };

  const menuItems = verificationStatus === 'verified' ? [
    { name: 'Dashboard', path: '/mitarbeiter/dashboard', icon: LayoutDashboard },
    { name: 'Meine Aufträge', path: '/mitarbeiter/auftrage', icon: Briefcase },
    { name: 'Verifizierung', path: '/mitarbeiter/verification', icon: UserCheck },
    { name: 'Qualifikation / Quiz', path: '/mitarbeiter/quiz', icon: Award },
    { name: 'Einstellungen', path: '/mitarbeiter/einstellungen', icon: Settings }
  ] : [
    { name: 'Verifizierung', path: '/mitarbeiter/verification', icon: ShieldAlert }
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity" onClick={() => setSidebarOpen(false)}></div>
      )}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-primary text-slate-300 flex flex-col shadow-xl transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center justify-between px-6 border-b border-secondary/50">
          <div className="flex items-center">
            <span className="text-2xl font-bold text-white tracking-tight">
              RM Digital<span className="text-accent">.</span>
            </span>
            <span className="ml-2 text-[10px] bg-accent text-primary px-2 py-0.5 rounded-full font-bold">TESTER</span>
          </div>
          <button className="md:hidden text-slate-300 hover:text-white" onClick={() => setSidebarOpen(false)}>
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)} className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${isActive ? 'bg-accent/10 text-accent font-medium' : 'hover:bg-secondary/50 hover:text-white'}`}>
                <Icon className={`w-5 h-5 ${isActive ? 'text-accent' : 'text-slate-400'}`} />
                {item.name}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-secondary/50">
          <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 w-full rounded-md text-slate-400 hover:bg-secondary/50 hover:text-white transition-colors">
            <LogOut className="w-5 h-5" />
            Abmelden
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 bg-white border-b border-border flex items-center justify-between px-4 sm:px-8 shadow-sm z-10 shrink-0">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-2 -ml-2 text-primary hover:bg-slate-100 rounded-md" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-semibold text-primary truncate">
              {menuItems.find((i) => location.pathname === i.path)?.name || 'Tester Panel'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {verificationStatus !== 'verified' && verificationStatus !== 'loading' && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-bold hidden sm:inline-block">Aktion erforderlich</span>
            )}
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-primary font-bold shrink-0">T</div>
          </div>
        </header>
        <div className="flex-1 overflow-auto p-4 sm:p-8">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
